import type { AxiosError } from 'axios'

export interface ApiErrorResponse {
  code: string
  message: string
  details?: Record<string, unknown>
}

export interface AppApiError extends Error {
  code?: string | null
  details?: Record<string, string[]>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function parseApiErrorResponse(value: unknown): ApiErrorResponse | null {
  if (!isRecord(value)) return null
  const code = value.code
  const message = value.message
  if (typeof code !== 'string' || typeof message !== 'string') return null
  const details = isRecord(value.details) ? value.details : undefined
  return { code, message, details }
}

function normalizeApiErrorDetails(details: Record<string, unknown> | undefined): Record<string, string[]> | undefined {
  if (!details) return undefined

  const normalized = Object.entries(details).reduce<Record<string, string[]>>((acc, [key, value]) => {
    if (Array.isArray(value)) {
      const items = value.filter((item): item is string => typeof item === 'string')
      if (items.length > 0) {
        acc[key] = items
      }
    } else if (typeof value === 'string') {
      acc[key] = [value]
    }
    return acc
  }, {})

  return Object.keys(normalized).length > 0 ? normalized : undefined
}

export function createApiError(error: unknown, fallback: string): AppApiError {
  if (error instanceof Error) {
    const axiosError = error as AxiosError
    const apiError = parseApiErrorResponse(axiosError.response?.data)
    const appError = new Error(apiError?.message || error.message || fallback) as AppApiError
    appError.code = apiError?.code ?? null
    appError.details = normalizeApiErrorDetails(apiError?.details)
    return appError
  }

  return new Error(fallback) as AppApiError
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  return createApiError(error, fallback).message
}

export function getApiErrorCode(error: unknown): string | null {
  if (error instanceof Error) {
    const directCode = (error as AppApiError).code
    if (typeof directCode === 'string') return directCode
  }
  if (!(error instanceof Error)) return null
  const axiosError = error as AxiosError
  const apiError = parseApiErrorResponse(axiosError.response?.data)
  return apiError?.code ?? null
}

export function getApiErrorDetails(error: unknown): Record<string, string[]> | null {
  if (error instanceof Error) {
    const directDetails = (error as AppApiError).details
    if (directDetails) return directDetails
  }
  if (!(error instanceof Error)) return null
  const axiosError = error as AxiosError
  const apiError = parseApiErrorResponse(axiosError.response?.data)
  return normalizeApiErrorDetails(apiError?.details) ?? null
}
